import React, { useCallback, useState } from "react";
import debounce from "lodash.debounce";

import { useTheme } from "@catchoftheday/theme";
import { useUID } from "@catchoftheday/utilities";

import { Box, BoxProps } from "../Box";
import {
  Popover,
  PopoverContent,
  PopoverControl,
  PopoverControlChildrenProps,
  PopoverPlacement,
} from "../Popover";

export type TooltipProps = {
  /** Reference component to trigger tooltip */
  reference: React.ReactNode;
  /** Preferred position of Tooltip  */
  placement?: PopoverPlacement;
  /** specifies offset in X axis from the tooltip to the position point \
   * if X axis is parallel to the placement direction, then + represent the placement direction \
   * otherwise, + represent right side
   */
  offsetX?: number;
  /** specifies offset in Y axis from the tooltip to the position point \
   * if Y axis is parallel to the placement direction, then + represent the placement direction \
   * otherwise, + represent bottom side
   */
  offsetY?: number;
} & BoxProps;

export const Tooltip: React.FC<TooltipProps> = ({
  children,
  reference,
  placement = "bottom",
  offsetX = 0,
  offsetY = 0,
  ...boxProps
}) => {
  const [tooltipShow, setTooltipShow] = useState(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSetTooltipShow = useCallback(debounce(setTooltipShow, 50), []);
  const theme = useTheme();
  const popperElementId = useUID();

  const hideTooltip = () => {
    debouncedSetTooltipShow.cancel();
    debouncedSetTooltipShow(false);
  };

  function onMouseLeaveHandler() {
    hideTooltip();
  }

  function onMouseEnterHandler() {
    debouncedSetTooltipShow.cancel();
    setTooltipShow(true);
  }

  function onKeyDownHandler(event: React.KeyboardEvent<HTMLDivElement>) {
    if (tooltipShow && event.key === "Escape") {
      hideTooltip();
    }
  }

  const offset = /^(bottom|top)/.test(placement)
    ? [offsetX, offsetY]
    : [offsetY, offsetX];

  const referenceElement: React.FC<PopoverControlChildrenProps> = ({ ref }) => {
    return (
      <Box
        ref={ref}
        display="inline-block"
        onFocus={onMouseEnterHandler}
        onMouseEnter={onMouseEnterHandler}
        onClick={onMouseEnterHandler}
        onBlur={onMouseLeaveHandler}
        onMouseLeave={onMouseLeaveHandler}
        onKeyDown={onKeyDownHandler}
        aria-describedby={tooltipShow ? popperElementId : undefined}
      >
        {reference}
      </Box>
    );
  };

  const popperElement = () => {
    return (
      <Box
        bg="white"
        border="1px solid"
        borderColor={theme?.colors?.["ui"]?.["borderColor"]}
        borderRadius="4px"
        p="10px"
        boxShadow={theme?.shadows?.["toolTip"]}
        onFocus={onMouseEnterHandler}
        onMouseEnter={onMouseEnterHandler}
        onBlur={onMouseLeaveHandler}
        onMouseLeave={onMouseLeaveHandler}
        role="tooltip"
        id={popperElementId}
      >
        {children}
      </Box>
    );
  };

  return (
    <Popover
      isOpen={tooltipShow}
      display="inline-block"
      placement={placement}
      hideArrow
      hideOnClickOutside
      modifier={[
        {
          name: "offset",
          enabled: true,
          options: {
            offset,
          },
        },
      ]}
      {...boxProps}
    >
      <PopoverControl>{referenceElement}</PopoverControl>
      <PopoverContent>{popperElement}</PopoverContent>
    </Popover>
  );
};

Tooltip.displayName = "Tooltip";

export default Tooltip;
